import React, { useState, useMemo } from 'react';
import { Search, Star, ThumbsUp, ThumbsDown, Check, X, Flag, MessageSquare } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDistanceToNow } from 'date-fns';
import { mockReviews, getUserById, getFirmById, getPendingReviews } from '@/data/mock';
import { toast } from 'sonner';

export const ReviewsPage = () => {
  const [reviews, setReviews] = useState(mockReviews);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [adminReply, setAdminReply] = useState('');
  const [deleteReview, setDeleteReview] = useState(null);

  const pendingCount = useMemo(() => reviews.filter(r => r.status === 'pending').length, [reviews]);
  const approvedToday = useMemo(() => {
    return reviews.filter(r => {
      const today = new Date().toDateString();
      const reviewDate = new Date(r.createdAt).toDateString();
      return r.status === 'approved' && reviewDate === today;
    }).length;
  }, [reviews]);
  const rejectedThisWeek = useMemo(() => reviews.filter(r => r.status === 'rejected').length, [reviews]);

  const filteredReviews = useMemo(() => {
    return reviews.filter(review => {
      const matchesTab = activeTab === 'all' || review.status === activeTab;
      const searchLower = searchTerm.toLowerCase();
      const user = getUserById(review.userId);
      const firm = getFirmById(review.firmId);
      const matchesSearch = 
        user?.name.toLowerCase().includes(searchLower) ||
        firm?.name.toLowerCase().includes(searchLower) ||
        review.reviewText.toLowerCase().includes(searchLower);
      return matchesTab && matchesSearch;
    });
  }, [reviews, activeTab, searchTerm]);

  const handleApprove = (reviewId) => {
    setReviews(reviews.map(r => 
      r.id === reviewId ? { ...r, status: 'approved' } : r
    ));
    toast.success('Review approved');
    setSelectedReview(null);
  };

  const handleReject = (reviewId) => {
    setReviews(reviews.map(r => 
      r.id === reviewId ? { ...r, status: 'rejected' } : r
    ));
    toast.success('Review rejected');
    setSelectedReview(null);
  };

  const handleBulkAction = (action) => {
    const newStatus = action === 'approve' ? 'approved' : 'rejected';
    setReviews(reviews.map(r => 
      selectedReviews.includes(r.id) ? { ...r, status: newStatus } : r
    ));
    toast.success(`${selectedReviews.length} reviews ${action}d`);
    setSelectedReviews([]);
  };

  return (
    <div>
      <PageHeader
        title="Reviews"
        description="Manage user reviews and ratings"
        breadcrumbs={['Admin', 'Community', 'Reviews']}
      />

      {/* Stats Row */}
      <div className="grid gap-6 md:grid-cols-4 mb-6">
        <StatsCard
          title="Pending"
          value={pendingCount}
          icon={Star}
          iconBg="bg-amber-100 text-amber-600"
        />
        <StatsCard
          title="Approved Today"
          value={approvedToday}
          icon={Check}
          iconBg="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          title="Rejected This Week"
          value={rejectedThisWeek}
          icon={X}
          iconBg="bg-red-100 text-red-600"
        />
        <StatsCard
          title="Total Reviews"
          value={reviews.length}
          icon={MessageSquare}
          iconBg="bg-blue-100 text-blue-600"
        />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {/* Filters */}
          <div className="flex items-center justify-between mb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="pending">
                  Pending
                  {pendingCount > 0 && (
                    <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs bg-amber-500">
                      {pendingCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="approved">Approved</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
                <TabsTrigger value="flagged">Flagged</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedReviews.length > 0 && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedReviews.length} review{selectedReviews.length > 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => handleBulkAction('approve')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Check className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('reject')}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {/* Reviews List */}
          <div className="space-y-4">
            {filteredReviews.map((review) => {
              const user = getUserById(review.userId);
              const firm = getFirmById(review.firmId);
              
              return (
                <div
                  key={review.id}
                  className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedReviews.includes(review.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedReviews([...selectedReviews, review.id]);
                        } else {
                          setSelectedReviews(selectedReviews.filter(id => id !== review.id));
                        }
                      }}
                    />
                    
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                    </Avatar>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                          <Badge variant="outline" className="text-xs">
                            {user?.tier}
                          </Badge>
                          <span className="text-slate-400">•</span>
                          <div className="flex items-center gap-1">
                            <img src={firm?.logo} alt="" className="h-5 w-5 rounded" />
                            <span className="text-sm text-slate-700 font-medium">{firm?.name}</span>
                          </div>
                        </div>
                        <StatusBadge status={review.status} />
                      </div>

                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`star-${i}`}
                              className={`h-4 w-4 ${
                                i < review.rating
                                  ? 'fill-amber-400 text-amber-400'
                                  : 'text-slate-300'
                              }`}
                            />
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Badge variant="outline">Support: {review.subRatings.support}/5</Badge>
                          <Badge variant="outline">Payouts: {review.subRatings.payouts}/5</Badge>
                          <Badge variant="outline">Platform: {review.subRatings.platform}/5</Badge>
                          <Badge variant="outline">Rules: {review.subRatings.rules}/5</Badge>
                        </div>
                      </div>

                      <p className="text-sm text-slate-700 mb-2 line-clamp-2">
                        {review.reviewText}
                      </p>

                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}</span>
                        <span className="flex items-center gap-1">
                          <ThumbsUp className="h-3 w-3" />
                          {review.helpful}
                        </span>
                        <span className="flex items-center gap-1">
                          <ThumbsDown className="h-3 w-3" />
                          {review.notHelpful}
                        </span>
                      </div>

                      {review.status === 'flagged' && review.flagReason && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                          <Flag className="h-3 w-3 inline mr-1" />
                          {review.flagReason}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {review.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(review.id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(review.id)}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSelectedReview(review)}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}

            {filteredReviews.length === 0 && (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No reviews found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Review Detail Sheet */}
      <Sheet open={!!selectedReview} onOpenChange={() => setSelectedReview(null)}>
        <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
          {selectedReview && (
            <>
              <SheetHeader>
                <SheetTitle>Review Details</SheetTitle>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                {/* Reviewer Info */}
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={getUserById(selectedReview.userId)?.avatar} />
                    <AvatarFallback>
                      {getUserById(selectedReview.userId)?.name?.[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-slate-900">
                      {getUserById(selectedReview.userId)?.name}
                    </p>
                    <p className="text-sm text-slate-600">
                      {getUserById(selectedReview.userId)?.email}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Firm */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">FIRM</p>
                  <div className="flex items-center gap-2">
                    <img
                      src={getFirmById(selectedReview.firmId)?.logo}
                      alt=""
                      className="h-8 w-8 rounded"
                    />
                    <span className="font-medium">
                      {getFirmById(selectedReview.firmId)?.name}
                    </span>
                  </div>
                </div>

                {/* Rating */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">OVERALL RATING</p>
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={`star-${i}`}
                          className={`h-5 w-5 ${
                            i < selectedReview.rating
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-slate-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-lg font-semibold">{selectedReview.rating}/5</span>
                  </div>
                </div>

                {/* Sub-ratings */}
                <div>
                  <p className="text-xs text-muted-foreground mb-3">SUB-RATINGS</p>
                  <div className="space-y-2">
                    {Object.entries(selectedReview.subRatings).map(([key, value]) => (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{key}</span>
                          <span className="font-medium">{value}/5</span>
                        </div>
                        <Progress value={value * 20} className="h-2" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Review Text */}
                <div>
                  <p className="text-xs text-muted-foreground mb-2">REVIEW</p>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {selectedReview.reviewText}
                  </p>
                </div>

                {/* Helpful counts */}
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <ThumbsUp className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm font-medium">{selectedReview.helpful} helpful</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ThumbsDown className="h-4 w-4 text-red-600" />
                    <span className="text-sm font-medium">{selectedReview.notHelpful} not helpful</span>
                  </div>
                </div>

                {/* Admin Reply */}
                {selectedReview.adminReply ? (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs font-semibold text-blue-900 mb-1">Admin Response</p>
                    <p className="text-sm text-blue-800">{selectedReview.adminReply}</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">POST ADMIN REPLY</p>
                    <Textarea
                      placeholder="Write your response..."
                      value={adminReply}
                      onChange={(e) => setAdminReply(e.target.value)}
                      rows={4}
                    />
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        toast.success('Reply posted');
                        setAdminReply('');
                      }}
                    >
                      Post Reply
                    </Button>
                  </div>
                )}

                <Separator />

                {/* Actions */}
                <div className="flex gap-2">
                  {selectedReview.status === 'pending' && (
                    <>
                      <Button
                        onClick={() => handleApprove(selectedReview.id)}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(selectedReview.id)}
                        variant="outline"
                        className="flex-1"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </>
                  )}
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteReview(selectedReview)}
                  >
                    Delete Review
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!deleteReview}
        onOpenChange={() => setDeleteReview(null)}
        title="Delete Review"
        description={`Are you sure you want to delete this review by ${getUserById(deleteReview?.userId)?.name}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          setReviews(reviews.filter(r => r.id !== deleteReview.id));
          setSelectedReview(null);
          toast.success('Review deleted');
          setDeleteReview(null);
        }}
      />
    </div>
  );
};
